import { useForm } from '@tanstack/react-form';
import { Button } from '@sara/ui/components/button';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import { Input } from '@sara/ui/components/input';
import { createWebhookFormSchema, type CreateWebhookFormValues } from './schemas';

interface CreateWebhookFormProps {
  onSubmit: (values: CreateWebhookFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string | null;
}

export function CreateWebhookForm({
  onSubmit,
  onCancel,
  isPending,
  error,
}: CreateWebhookFormProps) {
  const form = useForm({
    defaultValues: { url: '', label: '' } as CreateWebhookFormValues,
    validators: { onSubmit: createWebhookFormSchema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-5"
      autoComplete="off"
    >
      <form.Field name="url">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name} className="text-[13px] font-medium text-foreground">
                URL
              </FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  type="url"
                  placeholder="https://hooks.example.com/ai-interviewer"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending}
                  aria-invalid={hasError}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  The HTTPS endpoint that will receive signed webhook deliveries.
                </p>
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="label">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name} className="text-[13px] font-medium text-foreground">
                Label (optional)
              </FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  placeholder="e.g. Primary ATS"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending}
                  aria-invalid={hasError}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Helps you identify this receiver. Only used in the console.
                </p>
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/8 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          shape="pill"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="tinted"
          shape="pill"
          size="sm"
          className="gap-1.5"
          disabled={isPending}
        >
          {isPending ? 'Creating…' : 'Create webhook'}
        </Button>
      </div>
    </form>
  );
}
